import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Popup from 'reactjs-popup';
import { toastError } from '../../../../shared/toastHelper';
import usePledges        from './usePledges';
import PledgeRollupBar   from './PledgeRollupBar';
import PledgeList        from './PledgeList';
import PledgeFormModal   from './PledgeFormModal';
import crmLabels         from '../../utils/crmLabels';

const FILTER_OPTIONS = [
  { label: 'All',            value: '' },
  { label: 'Active',         value: 'active' },
  { label: 'Partially Paid', value: 'partially_paid' },
  { label: 'Delinquent',     value: 'delinquent' },
  { label: 'Fulfilled',      value: 'fulfilled' },
  { label: 'Written Off',    value: 'written_off' },
];

// The donor is a fan OR an organization — pass exactly one of fan / organization.
export default function PledgesTab({ team, fan, organization, canManage }) {
  const {
    pledges, rollup, loading, refresh, filters, setFilters,
    createPledge, updatePledge, cancelPledge, previewSchedule,
  } = usePledges({ teamId: team.id, fanId: fan?.id, organizationId: organization?.id });

  const [modalMode, setModalMode]       = useState(null);
  const [editingPledge, setEditingPledge] = useState(null);
  const [filterOpen, setFilterOpen]     = useState(false);
  const filterRef                        = useRef(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  const activeStatus = filters.q?.status_eq || '';

  // Close popover on outside click
  useEffect(() => {
    if (!filterOpen) return;
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filterOpen]);

  const handleFilterSelect = (value) => {
    setFilters(value ? { q: { status_eq: value } } : {});
    setFilterOpen(false);
  };

  const openEditFor = async (pledge) => {
    try {
      const { data } = await axios.get(`/teams/${team.id}/crm/pledges/${pledge.id}.json`);
      setEditingPledge(data);
      setModalMode('edit');
    } catch (e) {
      toastError('Could not load pledge details. Refresh and try again.');
    }
  };

  const handleCancelConfirmed = async () => {
    if (!cancelTarget) return;
    try {
      await cancelPledge(cancelTarget.id);
    } catch (e) {
      toastError('Could not cancel pledge. Refresh and try again.');
    } finally {
      setCancelTarget(null);
    }
  };

  const filterButtonClass = [
    'btn',
    'btn-setlist-secondary',
    activeStatus ? 'pt-filter-active' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <PledgeRollupBar rollup={rollup} />

      <div className="pt-section">
        <div className="pt-section__header">
          <div>
            <h2 className="pt-section__title">Pledges</h2>
            <div className="pt-section__sub">Multi-year commitments and installment plans</div>
          </div>
          <div className="pt-section__actions">
            <div className="pt-filter-anchor" ref={filterRef}>
              <button
                className={filterButtonClass}
                onClick={() => setFilterOpen((o) => !o)}
              >
                Filter{activeStatus ? ' ✓' : ''}
              </button>
              {filterOpen && (
                <div className="pt-filter-popover">
                  {FILTER_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      className={[
                        'pt-filter-option',
                        activeStatus === opt.value ? 'pt-filter-option--selected' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => handleFilterSelect(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {canManage && (
              <button
                className="btn btn-setlist-primary"
                onClick={() => { setEditingPledge(null); setModalMode('create'); }}
              >
                + New pledge
              </button>
            )}
          </div>
        </div>

        <PledgeList
          pledges={pledges}
          loading={loading}
          teamId={team.id}
          canManage={canManage}
          onEdit={openEditFor}
          onCancel={setCancelTarget}
        />
      </div>

      {modalMode && (
        <PledgeFormModal
          mode={modalMode}
          team={team}
          fan={fan}
          organization={organization}
          pledge={editingPledge}
          previewSchedule={previewSchedule}
          createPledge={createPledge}
          updatePledge={updatePledge}
          onClose={() => { setModalMode(null); refresh(); }}
          onSaved={() => { setModalMode(null); refresh(); }}
        />
      )}

      <Popup
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        modal
        closeOnDocumentClick
        contentStyle={{
          border: '1px solid var(--sl-border-secondary)',
          borderRadius: '8px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          width: '400px',
          padding: '24px',
        }}
      >
        {(close) => (
          <div>
            <h5 style={{ marginBottom: '12px', fontWeight: 600 }}>Cancel pledge?</h5>
            <p style={{ fontSize: '14px', color: 'var(--sl-text-secondary)', marginBottom: '20px' }}>
              This pledge will be removed from this {organization ? crmLabels(team).organization.singular.toLowerCase() : 'contact'}&#39;s pledge list but kept in history for reporting.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn btn-setlist-secondary" onClick={close}>Keep pledge</button>
              <button
                className="btn btn-setlist-primary"
                style={{ background: 'var(--sl-text-error)', borderColor: 'var(--sl-text-error)' }}
                onClick={() => { close(); handleCancelConfirmed(); }}
              >
                Cancel pledge
              </button>
            </div>
          </div>
        )}
      </Popup>
    </>
  );
}
